import {Component, DestroyRef, inject, Input} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormGroup} from '@angular/forms';
import {TableLazyLoadEvent} from 'primeng/table';
import {AccountService} from '../../../../../../../services/account.service';
import {DeviceService} from '../../../../../../../services/device.service';
import {LogService} from '../../../../../../../services/log.service';
import {InputSelectComponent} from '../../../../../../../shared/components/input-select/input-select';
import {TableComponent} from '../../../../../../../shared/components/table/table';
import {dateToString} from '../../../../../../../shared/utils/date.util';
import {SerializedAccountDto} from '../../../../../../../types/dtos/account.dto';
import {DeviceDto} from '../../../../../../../types/dtos/device.dto';
import {LogListDto, LogRow} from '../../../../../../../types/dtos/log.dto';
import {LoggingActionEnum, LoggingTargetEnum} from '../../../../../../../types/enums/logging.enum';
import {InputSelectInterface} from '../../../../../../../types/interfaces/input.interface';
import {TableConfig, TableRowDto} from '../../../../../../../types/interfaces/table.interface';
import {DashboardBoxComponent} from '../../../../../../dashboard/dashboard-box/dashboard-box.component';
import {ReportLogRow} from '../../report-rows.dto';
import {TranslateService} from '@ngx-translate/core';


@Component({
  selector: 'app-report-filter-log',
  imports: [
    TableComponent,
    InputSelectComponent,
    DashboardBoxComponent,
  ],
  templateUrl: './filter-log.html',
  styleUrl: './filter-log.css',
})
export class ReportFilterLog {
  @Input()
  public dateFrom: Date | null = null;

  @Input()
  public dateTo: Date | null = null;

  @Input({required: true}) formGroup!: FormGroup;
  public loading: boolean = true;
  public tableRows: TableRowDto<ReportLogRow>[] = [];
  public readonly tableConfig: TableConfig<ReportLogRow> = {
    columns: [
      {
        key: 'created_at',
        isTranslated: true,
        title: 'common.table.time',
        sortable: true,
      },
      {
        key: 'username',
        isTranslated: true,
        title: 'common.table.username',
        sortable: true,
      },
      {
        key: 'route',
        title: 'Route',
        sortable: true,
      },
      {
        key: 'action',
        isTranslated: true,
        title: 'common.table.label.action',
        sortable: true,
      },
      {
        key: 'target',
        isTranslated: true,
        title: 'common.table.target',
        sortable: true,
      },
      {
        key: 'target_id',
        isTranslated: true,
        title: 'common.table.target-id',
        overrideValue: (row: LogRow) => {
          if (!row.target_id) {
            return '-';
          }
          if (row.target === LoggingTargetEnum.DEVICE) {
            return this.getDeviceString(row.target_id);
          }
          if (row.target === LoggingTargetEnum.USER) {
            return this.getUserName(row.target_id);
          }
          return row.target_id?.toString() ?? '-';
        },
      },
    ],
    mobile: {
      title: {
        key: 'action',
      },
      description: {
        key: 'route',
      },
      sections: [
        {
          key: 'username',
          isTranslated: true,
          title: 'common.fields.username',
        },
        {
          key: 'target',
          isTranslated: true,
          title: 'common.table.target',
        },
        {
          key: 'target_id',
          isTranslated: true,
          title: 'common.table.target-id',
          overrideValue: (row: LogRow) => {
            if (!row.target_id) {
              return '-';
            }
            if (row.target === LoggingTargetEnum.DEVICE) {
              return this.getDeviceString(row.target_id);
            }
            if (row.target === LoggingTargetEnum.USER) {
              return this.getUserName(row.target_id);
            }
            return row.target_id?.toString() ?? '-';
          },
        },
      ],
    },
    pagination: true,
    showRows: 25,
    rowsPerPageOptions: [
      25,
    ],
    search: {
      label: 'Alarme durchsuchen...',
      id: 'report-alarm-global-search',
    },
    scrollHeight: '35vh',
    emptyMessage: 'Keine Alarme vorhanden',
    lazy: true,
    onLazyLoad: () => {
      this.loadLogs();
    },
  };
  protected readonly LoggingTargetEnum = LoggingTargetEnum;
  private userOptions: InputSelectInterface['options'] = [];
  private deviceOptions: InputSelectInterface['options'] = [];
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _logService: LogService,
    private readonly _accountService: AccountService,
    private readonly _deviceService: DeviceService,
    private readonly _translateService: TranslateService,
  ) {
    this._accountService.accountList$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data: SerializedAccountDto[]) => {
        this.userOptions = data.map((account) => ({
          label: account.full_name ?? account.username ?? 'Unknown',
          value: account.id.toString(),
        }));
      });

    this._deviceService.devices$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data: DeviceDto[]) => {
        this.deviceOptions = data.map((device) => ({
          label: device.serial_number ?? device.device_eui,
          value: device.id.toString(),
        }));
      });
  }

  public get tagetFilter(): LoggingTargetEnum | undefined {
    return this.formGroup.get('targetSelect')?.value as LoggingTargetEnum;
  }

  public get cuntLabel(): string {
    return `${this.tableConfig.totalRecords} ${this._translateService.instant('common.label.logs-found')}`;
  }

  public get userFilterConfig(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.fields.username',
      id: 'userSelect',
      iconLeft: 'account_circle',
      showClear: true,
      options: this.userOptions,
      onInput: () => this.loadLogs(),
    };
  }

  public get deviceFilterConfig(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.table.device.label',
      id: 'deviceSelect',
      iconLeft: 'memory',
      showClear: true,
      options: this.deviceOptions,
      virtualScroll: true,
      filter: true,
      required: false,
      onInput: () => this.loadLogs(),
    };
  }

  public get actionFilterConfig(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.table.label.action',
      id: 'actionSelect',
      iconLeft: 'history',
      showClear: true,
      options: Object.values(LoggingActionEnum)
        .sort()
        .map((action) => ({
          label: action.slice(0, 1)
            .toUpperCase() + action.slice(1),
          value: action.toLowerCase(),
        })),
      onInput: () => this.loadLogs(),
    };
  };

  public get targetFilterConfig(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.label.resource',
      id: 'targetSelect',
      iconLeft: 'category',
      showClear: true,
      options: Object.values(LoggingTargetEnum)
        .sort()
        .map((target) => ({
          label: target.slice(0, 1)
            .toUpperCase() + target.slice(1),
          value: target.toLowerCase(),
        })),
      onInput: () => this.loadLogs(),
    };
  };

  public getUserName(account_id: number): string {
    return this._accountService.getUsernameOfUserId(account_id);
  }

  public getDeviceString(device_id: number): string {
    const device: DeviceDto | null = this._deviceService.getLocalById(device_id);
    return device?.serial_number ?? device?.device_eui ?? 'Unknown';
  }

  private async loadLogs(
    /*limit: number = this.limit,
    offset: number = this.offset,*/
  ): Promise<void> {
    this.loading = true;

    const {
      userSelect,
      actionSelect,
      targetSelect,
      deviceSelect,
    } = this.formGroup.value;

    this._logService.requestLog({
      filter: {
        ...(userSelect ? {accountId: [Number(userSelect)]} : {}),
        ...(actionSelect ? {action: actionSelect} : {}),
        ...(targetSelect ? {target: targetSelect} : {}),
        ...(targetSelect == LoggingTargetEnum.DEVICE && deviceSelect ? {targetId: Number(deviceSelect)} : {}),
      },
    })
      .subscribe({
        next: (result: LogListDto) => {
          this.tableConfig.totalRecords = result.rowCount!;
          this.tableRows = result.data.map(log => {
            return {
              id: log.id,
              created_at: dateToString(log.created_at),
              username: log.account_id ? this.getUserName(log.account_id) : '',
              route: log.route,
              action: log.action ?? '-',
              target: log.target,
              target_id: log.target_id,
            };
          });
        },
        complete: () => this.loading = false,
        error: (error: any) => console.error('Error fetching logs:', error),
      });
  }
}
